"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

const GA_ID = "G-R0XG7JFNJN";
const CONSENT_COOKIE_NAME = "storita_cookie_consent";
const PROD_HOSTS = ["storita.ai", "www.storita.ai"];

function getCookie(name: string) {
  if (typeof document === "undefined") {
    return null;
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(encodedName));

  return cookie ? decodeURIComponent(cookie.slice(encodedName.length)) : null;
}

function setCookie(name: string, value: string, expiresInDays: number) {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function loadGA(sendConsentEvent = false) {
  if (typeof window === "undefined" || !PROD_HOSTS.includes(window.location.hostname)) {
    return;
  }

  if (typeof document === "undefined") {
    return;
  }

  if (document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${GA_ID}"]`)) {
    if (sendConsentEvent && (window as any).gtag) {
      (window as any).gtag("event", "cookie_consent_granted");
    }
    return;
  }

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  script.onload = () => {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    function gtag(...args: any[]) {
      w.dataLayer.push(arguments);
    }
    w.gtag = gtag;
    gtag("js", new Date());
    gtag("config", GA_ID);
    if (sendConsentEvent) {
      gtag("event", "cookie_consent_granted");
    }
  };
  document.head.appendChild(script);
}

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = getCookie(CONSENT_COOKIE_NAME);

    if (consent === "true") {
      loadGA();
      return;
    }

    if (consent !== "false") {
      setIsVisible(true);
    }
  }, []);

  const handleConsent = (accepted: boolean) => {
    setCookie(CONSENT_COOKIE_NAME, accepted ? "true" : "false", 365);

    if (accepted) {
      (window as any).storita_consent_granted = true;
      loadGA(true);
    }

    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] border-t border-zinc-800 bg-zinc-900/95 px-4 py-4 text-zinc-100 shadow-[0_-12px_40px_rgba(0,0,0,0.35)] backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm leading-relaxed text-zinc-100 md:max-w-3xl">
          This website uses cookies for analytics to help us improve our service.{" "}
          <Link href="/legal/cookie-policy" className="underline text-zinc-300 hover:text-white">
            Cookie Policy
          </Link>
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => handleConsent(false)}
            className="inline-flex items-center justify-center rounded-md border border-zinc-700 px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:border-zinc-500 hover:text-white"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => handleConsent(true)}
            className="inline-flex items-center justify-center rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-500"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
